/**
 * Blog list and helpers. Uses GraphQL posts API (posts in category "blog" / "blogi").
 * Falls back to REST API (blog CPT) when GraphQL fails (e.g. 404 on custom endpoint).
 */

import { BLOG_AUTHOR_FULL_NAMES } from '../constants/blog';
import { fetchBlogsFromRest } from './wordpressRest';
import { getBaseCategoryForLang, MEET_IDENTIANS_CATEGORY_EN, MEET_IDENTIANS_CATEGORY_FI } from '../constants/blogCategories';
import {
  GET_BLOG_POSTS_WITH_ACF,
  GET_BLOG_POSTS,
} from './queries/blogPost';

/**
 * Fetches blog list. Uses posts in category blog/blogi only (GraphQL).
 * @param {import('@apollo/client').ApolloClient} client - Apollo client
 * @param {{ categorySlug?: string, first?: number }} variables - first = page size; categorySlug overrides lang when set
 * @param {{ lang?: 'en' | 'fi' }} [options] - lang determines category: en => blog, fi => blogi
 * @returns {Promise<{ posts: Array, generalSettings: object|null, useAcfQuery: boolean }>}
 */
export async function fetchBlogListData(client, variables = {}, options = {}) {
  const first = variables?.first ?? 200;
  const lang = options?.lang === 'fi' ? 'fi' : 'en';
  const categorySlug = variables?.categorySlug ?? getBaseCategoryForLang(lang);
  let posts = [];
  let generalSettings = null;
  let useAcfQuery = false;
  if (!client) {
    return { posts: [], generalSettings: null, useAcfQuery: false };
  }
  try {
    const { data } = await client.query({
      query: GET_BLOG_POSTS_WITH_ACF,
      variables: { categorySlug, first },
    });
    posts = data?.posts?.nodes ?? [];
    generalSettings = data?.generalSettings ?? null;
    useAcfQuery = true;
  } catch (err) {
    try {
      const { data } = await client.query({
        query: GET_BLOG_POSTS,
        variables: { categorySlug, first },
      });
      posts = data?.posts?.nodes ?? [];
      generalSettings = data?.generalSettings ?? null;
    } catch (minErr) {
      console.error('[blogUtils] fetchBlogListData failed (GraphQL)', minErr);
      try {
        const restNodes = await fetchBlogsFromRest(first, lang);
        posts = restNodes ?? [];
      } catch (restErr) {
        console.error('[blogUtils] REST fallback failed', restErr);
      }
    }
  }
  return {
    posts: posts ?? [],
    generalSettings,
    useAcfQuery,
  };
}

export function normalizeBlogAuthor(name) {
  const s = name != null ? String(name).trim() : '';
  if (!s || s.toUpperCase() === 'DEBUG') return null;
  return s;
}

export function toFullAuthorName(name) {
  if (!name || typeof name !== 'string') return name || '';
  const t = name.trim();
  return BLOG_AUTHOR_FULL_NAMES[t] || t;
}

export function resolveAuthorShown(raw) {
  if (raw == null) return null;
  if (typeof raw === 'string' && raw.trim()) return raw.trim();
  if (Array.isArray(raw) && raw.length > 0) {
    const first = raw[0];
    if (typeof first === 'string' && first.trim()) return first.trim();
    if (first?.label) return first.label;
    if (first?.value) return first.value;
    if (first?.name && String(first.name).trim()) return first.name.trim();
  }
  if (raw?.label) return raw.label;
  if (raw?.value) return raw.value;
  return null;
}

export function isGraphQLError(err) {
  return err?.graphQLErrors?.length > 0 || err?.networkError;
}

export function stripHtml(html) {
  if (typeof html !== 'string') return '';
  let text = html.replace(/<[^>]+>/g, '');
  text = text
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&#\d+;/g, (match) => {
      const code = parseInt(match.replace(/&#|;/g, ''), 10);
      return String.fromCharCode(code);
    })
    .replace(/\s+/g, ' ')
    .trim();
  return text;
}

const MEET_OUR_TEAM_LIMIT = 3;
const MEET_OUR_TEAM_FETCH_FIRST = 50;

/**
 * Fetches up to 3 blog posts for "Tutustu identiolaisiin" / "Meet our team" section.
 * Uses posts from category tutustu-identiolaisiin (FI) or meet-identians (EN).
 */
export async function fetchMeetOurTeamPosts(client, lang = 'en') {
  const blogBase = lang === 'fi' ? '/blogi' : '/blog';
  const categorySlug = lang === 'fi' ? MEET_IDENTIANS_CATEGORY_FI : MEET_IDENTIANS_CATEGORY_EN;
  const { posts } = await fetchBlogListData(
    client,
    { first: MEET_OUR_TEAM_FETCH_FIRST, categorySlug },
    { lang: lang === 'fi' ? 'fi' : 'en' }
  );
  const source = Array.isArray(posts) ? posts : [];
  const allNodes = source.length > MEET_OUR_TEAM_LIMIT ? [...source] : source;
  if (allNodes.length > MEET_OUR_TEAM_LIMIT) {
    for (let i = allNodes.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allNodes[i], allNodes[j]] = [allNodes[j], allNodes[i]];
    }
  }
  const selected = allNodes.slice(0, MEET_OUR_TEAM_LIMIT);
  return selected.map((node) => {
    const image = node?.featuredImage?.node?.sourceUrl ?? null;
    const excerpt = typeof node?.excerpt === 'string' ? stripHtml(node.excerpt) : '';
    return {
      slug: node.slug,
      title: node.title ?? '',
      excerpt: excerpt || '',
      image,
      href: `${blogBase}/${node.slug}`,
    };
  });
}
